"use client";

import { BrandVoiceOption } from "@/app/app/(dashboard)/project/[id]/ai/content/[template-slug]/page";
import { Tabs, Tab } from "@heroui/react";
import { Autocomplete, AutocompleteItem } from "@heroui/react";
import { useState, useEffect, useRef } from "react";
import React from "react";
import { cn } from "@/lib/utils";
import { useLocalStorage } from "usehooks-ts";
interface BrandVoiceSelectorProps {
  selectedBrandVoice: string | null;
  onBrandVoiceChange: (value: string | null) => void;
  platformVoices: BrandVoiceOption[];
  customVoices: BrandVoiceOption[];
  className?: string;
  isCompact?: boolean;
  siteId: string;
  passedClassNames?: {
    base?: string;
    popoverContent?: string;
  };
}
export function BrandVoiceSelector({
  selectedBrandVoice,
  onBrandVoiceChange,
  platformVoices,
  customVoices,
  className = "",
  isCompact = false,
  siteId,
  passedClassNames
}: BrandVoiceSelectorProps) {
  const [inputValue, setInputValue] = useState<string>("");
  const [displayBrandVoice, setDisplayBrandVoice] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [voiceSource, setVoiceSource] = useState<"platform" | "custom" | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useLocalStorage(`brandVoiceTab-${siteId}`, "platform");

  // Calculate scrollbar width once on mount
  const scrollbarWidth = useRef<number>(0);
  useEffect(() => {
    // Calculate scrollbar width
    const getScrollbarWidth = () => {
      // Create a temporary div with overflow
      const outer = document.createElement("div");
      outer.style.visibility = "hidden";
      outer.style.overflow = "scroll";
      document.body.appendChild(outer);

      // Create an inner div
      const inner = document.createElement("div");
      outer.appendChild(inner);

      // Calculate the width difference
      const width = outer.offsetWidth - inner.offsetWidth;

      // Clean up
      outer.parentNode?.removeChild(outer);
      return width;
    };
    scrollbarWidth.current = getScrollbarWidth();
  }, []);

  // Handle scroll prevention without layout shift
  useEffect(() => {
    const bodyStyle = document.body.style;
    const previousOverflow = bodyStyle.overflow;
    const previousPaddingRight = bodyStyle.paddingRight;
    if (isHovering) {
      // Store current scroll position
      const scrollY = window.scrollY;

      // Disable scrolling but maintain layout by adding padding equal to scrollbar width
      bodyStyle.overflow = "hidden";
      bodyStyle.paddingRight = `${scrollbarWidth.current}px`;

      // Maintain scroll position
      window.scrollTo(0, scrollY);
    } else {
      // Restore scrolling and padding
      bodyStyle.overflow = "";
      bodyStyle.paddingRight = "";
    }

    // Cleanup function
    return () => {
      bodyStyle.overflow = previousOverflow;
      bodyStyle.paddingRight = previousPaddingRight;
    };
  }, [isHovering]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Restore scrolling when component unmounts
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      // Also clean up any potential overlay elements that might be left
      const overlays = document.querySelectorAll('[data-overlay="true"], [class*="nextui-overlay"]');
      overlays.forEach(overlay => {
        overlay.remove();
      });
    };
  }, []);

  // Process selectedBrandVoice to handle platform--- prefix
  useEffect(() => {
    if (selectedBrandVoice) {
      // Remove platform--- prefix if present for display purposes
      const displayValue = selectedBrandVoice.startsWith("platform---") ? selectedBrandVoice.replace("platform---", "") : selectedBrandVoice;
      setDisplayBrandVoice(displayValue);

      // Find the matching voice to get the correct key for selection
      let foundVoice: BrandVoiceOption | undefined;
      if (selectedBrandVoice.startsWith("platform---")) {
        // This is a platform voice, look it up by label in platformVoices
        const voiceName = selectedBrandVoice.replace("platform---", "");
        foundVoice = platformVoices.find(voice => voice.label === voiceName);
        if (foundVoice) {
          setSelectedKey(foundVoice.value);
        }
        setVoiceSource("platform");
      } else {
        // Check custom voices first
        foundVoice = customVoices.find(voice => voice.label === selectedBrandVoice);
        if (foundVoice) {
          setSelectedKey(foundVoice.value);
          setVoiceSource("custom");
        } else {
          // Then check platform voices
          foundVoice = platformVoices.find(voice => voice.label === selectedBrandVoice);
          if (foundVoice) {
            setSelectedKey(foundVoice.value);
            setVoiceSource("platform");
          } else {
            // If not found in either, treat it as a custom value
            setSelectedKey(selectedBrandVoice);
            setVoiceSource("custom");
          }
        }
      }

      // Set input value for custom inputs that aren't in the dropdowns
      if (!foundVoice) {
        setInputValue(displayValue);
      }
    } else {
      // Handle null/empty selectedBrandVoice
      setDisplayBrandVoice("");
      setSelectedKey("");
      setVoiceSource(null);
      setInputValue("");
    }
  }, [selectedBrandVoice, platformVoices, customVoices]);
  const handleBrandVoiceChange = (key: string | null) => {
    // Handle null/clear case
    if (key === null || key === "") {
      setSelectedKey("");
      setDisplayBrandVoice("");
      setVoiceSource(null);
      setInputValue("");
      onBrandVoiceChange(null);
      return;
    }
    setSelectedKey(key);

    // Find the selected voice to get the label
    let voiceLabel: string;
    let isPlatformVoice = false;

    // First check if it's in platform voices
    const platformVoice = platformVoices.find(v => v.value === key);
    if (platformVoice) {
      voiceLabel = platformVoice.label;
      isPlatformVoice = true;
      setVoiceSource("platform");
    } else {
      // Then check if it's in custom voices
      const customVoice = customVoices.find(v => v.value === key);
      if (customVoice) {
        voiceLabel = customVoice.label;
        setVoiceSource("custom");
      } else {
        // If not found in either, use the key as the label (custom input)
        voiceLabel = key;
        setVoiceSource("custom");
      }
    }

    // Update display immediately to prevent flashing
    setDisplayBrandVoice(voiceLabel);

    // Pass up to parent with platform--- prefix if needed
    if (isPlatformVoice) {
      onBrandVoiceChange(`platform---${voiceLabel}`);
    } else {
      onBrandVoiceChange(voiceLabel);
    }
  };
  return <div ref={selectorRef} className={`z-50 w-full ${!isCompact ? "flex flex-col gap-2" : ""} ${className}`} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} onFocus={() => setIsHovering(true)} onBlur={e => {
    // Only set hovering to false if focus is moving outside the component
    if (!selectorRef.current?.contains(e.relatedTarget as Node)) {
      setIsHovering(false);
    }
  }} data-sentry-component="BrandVoiceSelector" data-sentry-source-file="brand-voice-selector.tsx">
      {!isCompact && <span className="text-sm font-medium">Brand Voice</span>}
      <Tabs aria-label="Brand Voice Options" size="sm" variant="light" radius={isCompact ? "md" : "md"} classNames={{
      base: "w-full",
      tabList: isCompact ? "bg-default-100 h-8 mb-0 w-fit mx-auto" : "bg-default-100",
      cursor: "bg-background shadow-sm",
      tab: isCompact ? "" : "px-4 h-8",
      tabContent: "group-data-[selected=true]:text-default-foreground",
      panel: isCompact ? "p-1" : undefined
    }} selectedKey={activeTab} onSelectionChange={key => setActiveTab(key as string)} data-sentry-element="Tabs" data-sentry-source-file="brand-voice-selector.tsx">
        <Tab key="platform" title="Platform Voices" data-sentry-element="Tab" data-sentry-source-file="brand-voice-selector.tsx">
          <Autocomplete aria-label="Platform brand voice selector" placeholder="Select or type brand voice" selectedKey={activeTab === "platform" && voiceSource === "platform" ? selectedKey : undefined} value={activeTab === "platform" && voiceSource === "platform" ? displayBrandVoice : ""} onSelectionChange={key => {
          if (key === null || key === "") {
            handleBrandVoiceChange(null);
          } else {
            handleBrandVoiceChange(key as string);
          }
        }} onInputChange={value => {
          setInputValue(value);
          setDisplayBrandVoice(value);
        }} onKeyDown={e => {
          if (e.key === "Enter" && inputValue.trim()) {
            handleBrandVoiceChange(inputValue);
          }
        }} onOpenChange={isOpen => {
          // Lock scroll when dropdown is open
          setIsHovering(isOpen);
        }} className="w-full" size="md" variant="flat" defaultItems={platformVoices} items={platformVoices} allowsCustomValue isClearable={true} classNames={{
          base: `max-w-full ${passedClassNames?.base}`,
          popoverContent: `${passedClassNames?.popoverContent}`,
          listbox: isCompact ? "max-h-[200px]" : undefined
        }} data-sentry-element="Autocomplete" data-sentry-source-file="brand-voice-selector.tsx">
            {item => <AutocompleteItem key={item.value} textValue={item.label}>
                <div className={`flex flex-col ${isCompact ? "py-1" : ""}`}>
                  <span className="text-small">{item.label}</span>
                  {item.description && <span className={cn("line-clamp-4 text-tiny text-default-400", isCompact && "line-clamp-2")}>
                      {item.description}
                    </span>}
                </div>
              </AutocompleteItem>}
          </Autocomplete>
        </Tab>
        <Tab key="custom" title={`My Voices (${customVoices.length})`} data-sentry-element="Tab" data-sentry-source-file="brand-voice-selector.tsx">
          <Autocomplete aria-label="Custom brand voice selector" placeholder={customVoices.length > 0 ? "Select or type custom voice" : "No custom voices created yet"} selectedKey={activeTab === "custom" && voiceSource === "custom" ? selectedKey : undefined} value={activeTab === "custom" && voiceSource === "custom" ? displayBrandVoice : inputValue} onSelectionChange={key => {
          if (key === null || key === "") {
            handleBrandVoiceChange(null);
          } else {
            handleBrandVoiceChange(key as string);
          }
          setInputValue(""); // Clear input after selection
        }} onInputChange={value => {
          setInputValue(value);
          setDisplayBrandVoice(value);
        }} onKeyDown={e => {
          if (e.key === "Enter" && inputValue.trim()) {
            handleBrandVoiceChange(inputValue);
            setInputValue(""); // Clear input after submission
          }
        }} onOpenChange={isOpen => {
          // Lock scroll when dropdown is open
          setIsHovering(isOpen);
        }} className="w-full" size="md" variant="flat" defaultItems={customVoices} items={customVoices} allowsCustomValue isClearable={true} isDisabled={customVoices.length === 0} classNames={{
          base: "max-w-full",
          listbox: isCompact ? "max-h-[200px]" : undefined
        }} data-sentry-element="Autocomplete" data-sentry-source-file="brand-voice-selector.tsx">
            {item => <AutocompleteItem key={item.value} textValue={item.label}>
                <div className={`flex flex-col ${isCompact ? "py-1" : ""}`}>
                  <span className="text-small">{item.label}</span>
                  {item.description && <span className={cn("line-clamp-4 text-tiny text-default-400", isCompact && "line-clamp-2")}>
                      {item.description}
                    </span>}
                </div>
              </AutocompleteItem>}
          </Autocomplete>
        </Tab>
      </Tabs>
    </div>;
}